import { t } from '../../../shared/i18n';
// 记录内容的前端校验：与后端 DNSService::validateRecordContent 规则保持一致，
// 只是提前在输入时给出中文提示（后端仍会再校验一次）。改后端规则时同步改这里。

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const LABEL = /^(\*|[a-zA-Z0-9_]([a-zA-Z0-9_-]*[a-zA-Z0-9_])?)$/;

const isIPv6 = (v) => {
    if (!v.includes(':') || /[^0-9a-fA-F:.]/.test(v)) return false;
    try {
        // 浏览器的 URL 解析器会严格校验方括号内的 IPv6 字面量
        new URL(`http://[${v}]/`);
        return true;
    } catch {
        return false;
    }
};

const isHostname = (v) => {
    const h = v.replace(/\.$/, '');
    if (h === '@') return true;
    if (!h || h.length > 253) return false;
    return h.split('.').every((l) => l.length > 0 && l.length <= 63 && LABEL.test(l));
};

// 每种类型的格式说明，用作输入框占位提示
export const CONTENT_PLACEHOLDERS = {
    A: '例如 192.0.2.10',
    AAAA: '例如 2001:db8::10',
    CNAME: '例如 www.example.com',
    NS: '例如 ns1.example.com',
    PTR: '例如 host.example.com',
    MX: '邮件服务器域名，例如 mail.example.com（优先级单独填写）',
    TXT: '例如 v=spf1 include:_spf.example.com ~all',
    SRV: '权重 端口 目标，例如 5 5060 sip.example.com（优先级单独填写）',
    CAA: '标志 标签 值，例如 0 issue "letsencrypt.org"',
    HTTPS: '优先级 目标 [参数]，例如 1 . alpn=h2,h3',
    SVCB: '优先级 目标 [参数]，例如 1 svc.example.com port=8443',
    NAPTR: '顺序 偏好 "标志" "服务" "正则" 替换，例如 100 10 "U" "E2U+sip" "!^.*$!sip:info@example.com!" .',
};

// 返回错误提示；内容合法时返回 null
export const checkRecordContent = (type, raw) => {
    const recordType = String(type || '').toUpperCase();
    const v = String(raw ?? '');
    if (!v.trim()) return t('Enter the record value');
    // 与后端一致：任何类型都不允许换行、制表符等控制字符
    if (/[\x00-\x1f\x7f]/.test(v)) return t('Record content cannot contain control characters such as line breaks or tabs');
    const c = v.trim();
    switch (recordType) {
        case 'A':
            return IPV4.test(c) ? null : t('Please enter a valid IPv4 address, such as 192.0.2.10');
        case 'AAAA':
            return isIPv6(c) ? null : t('Please enter a valid IPv6 address, such as 2001:db8::10');
        case 'CNAME':
        case 'NS':
        case 'PTR':
            return isHostname(c) ? null : t('Enter a valid domain, e.g. www.example.com');
        case 'MX':
            return isHostname(c) ? null : t('Enter the mail server domain, e.g. mail.example.com (@ represents this domain)');
        case 'TXT':
        case 'SPF':
            return c.length <= 4096 ? null : t('TXT records support up to 4096 characters');
        case 'SRV':
            return /^\d+\s+\d+\s+\S+$/.test(c) ? null : t('Required format: weight port target, e.g., 5 5060 sip.example.com');
        case 'CAA':
            return /^\d+\s+(issue|issuewild|iodef)\s+.+$/i.test(c) ? null : t('Required format: flags tag value, e.g., 0 issue "letsencrypt.org"');
        case 'HTTPS':
        case 'SVCB':
            return /^\d+\s+\S+/.test(c) ? null : t('Required format: priority target [parameters], e.g., 1 . alpn=h2');
        case 'NAPTR':
            return /^\d+\s+\d+\s+"[^"]*"\s+"[^"]*"\s+"[^"]*"\s+\S+$/.test(c)
                ? null
                : t('Required format: order preference "flags" "service" "regexp" replacement');
        default:
            return null;
    }
};

// antd Form 规则：getType 返回当前记录类型（表单里的 type 或行记录的 type）
export const recordContentRule = (getType) => ({
    validator: (_, value) => {
        const msg = checkRecordContent(getType(), value);
        return msg ? Promise.reject(new Error(msg)) : Promise.resolve();
    },
});
